import {useMemo, useRef} from 'react'

import type {FetchOptions} from '../../@types/fetch'
import {fetchApiResource} from '../utils/fetchApi'

import {useEvent} from './useEvent'

interface Hook {
  readonly fetcher: (key: string, init?: FetchOptions) => any
  readonly abortAll: () => void
}

export function useFetcher(): Hook {
  const controllersRef = useRef(new Map<AbortController, null>())

  const fetcher = useEvent(async (key: string, init?: FetchOptions) => {
    const controller = new AbortController()
    controllersRef.current.set(controller, null)

    try {
      return await fetchApiResource(key, {
        ...init,
        signal: controller.signal,
      })
    } finally {
      controllersRef.current.delete(controller)
    }
  })

  const abortAll = useEvent((): void => {
    if (!controllersRef.current) {
      return
    }

    for (const controller of controllersRef.current.keys()) {
      controller.abort()
    }

    controllersRef.current.clear()
  })

  return useMemo(() => ({
    fetcher,
    abortAll,
  }), [fetcher, abortAll])
}
